/**
 * 벌점 기록 탭
 */
import React, { useState, useMemo, useEffect } from 'react';
import { PenaltyRecord, PenaltyType, PenaltyRecordFilter } from '../../models/Penalty';
import PenaltyDetailModal from './PenaltyDetailModal';
import { penaltySourceBadge } from '../../utils/penaltySource';

const PAGE_SIZE = 20;

interface PenaltyRecordsTabProps {
  records: PenaltyRecord[];
  types: PenaltyType[];
  total: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onCancelRecord: (recordId: string, reason: string) => Promise<void>;
  onReduceRecord: (recordId: string, reducedPoints: number, reason: string) => Promise<void>;
  onCancelReduction?: (recordId: string) => Promise<void>;
  onRestoreRecord?: (recordId: string) => Promise<void>;
  onRefresh: (filter?: PenaltyRecordFilter) => Promise<void>;
  onIssue: () => void;
}

export const PenaltyRecordsTab: React.FC<PenaltyRecordsTabProps> = ({
  records,
  types,
  onCancelRecord,
  onReduceRecord,
  onCancelReduction,
  onRestoreRecord,
  onRefresh,
  onIssue,
}) => {
  const [filter, setFilter] = useState<PenaltyRecordFilter>({
    include_cancelled: true,
  });
  const [cancelingId, setCancelingId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  // 상세보기 모달 (행 클릭 → 몇 시에 몇 분 지각했는지 등)
  const [detailRecordId, setDetailRecordId] = useState<string | null>(null);

  // 감경 모달 상태
  const [reducingRecord, setReducingRecord] = useState<PenaltyRecord | null>(null);
  const [reducePoints, setReducePoints] = useState<number>(1);
  const [reduceReason, setReduceReason] = useState('');

  // 필터 변경 핸들러
  const handleFilterChange = (key: keyof PenaltyRecordFilter, value: unknown) => {
    const newFilter = { ...filter, [key]: value };
    setFilter(newFilter);
    onRefresh(newFilter);
  };

  // 취소 처리
  const handleCancel = async () => {
    if (!cancelingId || !cancelReason.trim()) return;

    try {
      await onCancelRecord(cancelingId, cancelReason);
      setCancelingId(null);
      setCancelReason('');
    } catch (e) {
      console.error('벌점 취소 실패:', e);
    }
  };

  // 감경 모달 열기
  const openReduceModal = (record: PenaltyRecord) => {
    setReducingRecord(record);
    setReducePoints(1);
    setReduceReason('');
  };

  // 감경 처리
  const handleReduce = async () => {
    if (!reducingRecord || !reduceReason.trim() || reducePoints < 1) return;

    try {
      await onReduceRecord(reducingRecord.id, reducePoints, reduceReason);
      setReducingRecord(null);
      setReducePoints(1);
      setReduceReason('');
    } catch (e) {
      console.error('벌점 감경 실패:', e);
    }
  };

  // 유형 이름 찾기
  const getTypeName = (typeCode: string): string => {
    const type = types.find((t) => t.code === typeCode);
    return type?.name || typeCode;
  };

  // 감경 후 실제 점수 = 백엔드가 이미 points 를 순점수로 저장하므로 그대로 사용
  // (예전엔 points 에서 reduced_points 를 또 빼서 이중 차감 → 10점 감경 5점이 0점으로 표시되는 버그)
  const getEffectivePoints = (record: PenaltyRecord): number => record.points;

  // 감경 전 원 점수 (표시용) — original_points 우선, 없으면 순점수 + 누적 감경분
  const getOriginalPoints = (record: PenaltyRecord): number =>
    (record.original_points ?? (record.points + (record.reduced_points || 0)));

  // 추가로 더 감경 가능한 최대 점수 (현재 순점수 - 1)
  const getMaxReducePoints = (record: PenaltyRecord): number => record.points - 1;

  // 학생 이름 검색 (클라이언트 필터) + 클라이언트 페이지네이션
  const [studentSearch, setStudentSearch] = useState('');
  const [page, setPage] = useState(1);

  const filteredRecords = useMemo(() => {
    const q = studentSearch.trim().toLowerCase();
    if (!q) return records;
    return records.filter((r) => (r.student_name || '').toLowerCase().includes(q));
  }, [records, studentSearch]);

  useEffect(() => {
    setPage(1);
  }, [studentSearch, records]);

  const total = filteredRecords.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedRecords = filteredRecords.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const onPageChange = (p: number) => setPage(Math.min(Math.max(1, p), totalPages));

  return (
    <div className="space-y-4">
      {/* 필터 */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">학생 검색</label>
            <input
              type="text"
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
              placeholder="🔍 학생 이름"
              className="border rounded-lg px-3 py-2 text-sm w-40"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">벌점 유형</label>
            <select
              value={filter.penalty_type || ''}
              onChange={(e) => handleFilterChange('penalty_type', e.target.value || undefined)}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">전체</option>
              {types.map((type) => (
                <option key={type.id} value={type.code}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">시작일</label>
            <input
              type="date"
              value={filter.start_date || ''}
              onChange={(e) => handleFilterChange('start_date', e.target.value || undefined)}
              className="border rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">종료일</label>
            <input
              type="date"
              value={filter.end_date || ''}
              onChange={(e) => handleFilterChange('end_date', e.target.value || undefined)}
              className="border rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="flex items-center gap-2 mt-5">
            <input
              type="checkbox"
              id="include_cancelled"
              checked={filter.include_cancelled}
              onChange={(e) => handleFilterChange('include_cancelled', e.target.checked)}
              className="rounded"
            />
            <label htmlFor="include_cancelled" className="text-sm text-gray-600">
              취소된 기록 포함
            </label>
          </div>

          <button
            onClick={() => onRefresh(filter)}
            className="mt-5 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
          >
            검색
          </button>
        </div>
      </div>

      {/* 기록 목록 */}
      <div className="bg-white rounded-lg border">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="font-medium text-gray-900">상벌점 기록</h3>
          <button
            onClick={onIssue}
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            상벌점 부과
          </button>
        </div>

        {filteredRecords.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <svg
              className="w-12 h-12 mx-auto text-gray-300 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <p>{studentSearch.trim() ? `"${studentSearch.trim()}" 검색 결과가 없습니다.` : '상벌점 기록이 없습니다.'}</p>
          </div>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    학생
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    유형
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    점수
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    사유
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    발생일
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    방식
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    상태
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    작업
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pagedRecords.map((record) => (
                  <tr
                    key={record.id}
                    onClick={() => setDetailRecordId(record.id)}
                    title="클릭하면 상세(예정 시각·실제 도착 시각·지각 시간)를 볼 수 있습니다"
                    className={`hover:bg-blue-50 cursor-pointer ${record.is_cancelled ? 'opacity-50' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">
                        {record.student_name || '학생'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{getTypeName(record.penalty_type)}</td>
                    <td className="px-4 py-3 text-center">
                      {record.reduced_points && record.reduced_points > 0 ? (
                        <span className="font-medium">
                          <span className={`${
                            (record.point_type || 'demerit') === 'merit' ? 'text-blue-600' : 'text-red-600'
                          }`}>
                            {(record.point_type || 'demerit') === 'merit' ? '+' : '-'}{getEffectivePoints(record)}점
                          </span>
                          <span className="block text-xs text-indigo-500 mt-0.5">
                            (감경: {getOriginalPoints(record)}{'\u2192'}{getEffectivePoints(record)})
                          </span>
                        </span>
                      ) : (
                        <span className={`font-medium ${
                          (record.point_type || 'demerit') === 'merit' ? 'text-blue-600' : 'text-red-600'
                        }`}>
                          {(record.point_type || 'demerit') === 'merit' ? '+' : '-'}{record.points}점
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-sm max-w-xs truncate">
                      {/* 자율학습 구간에서 매겨진 벌점은 한눈에 구분되게 표시 (2026-07-28) */}
                      {record.reason?.startsWith('[자율학습]') ? (
                        <>
                          <span className="inline-block px-1.5 py-0.5 mr-1 text-xs bg-rose-100 text-rose-700 rounded align-middle">
                            자율학습
                          </span>
                          {record.reason.replace('[자율학습]', '').trim()}
                        </>
                      ) : (
                        record.reason
                      )}
                      {record.reduce_reason && (
                        <span className="block text-xs text-indigo-500 mt-0.5">
                          [감경 사유] {record.reduce_reason}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-500 text-sm">
                      {record.occurred_date}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          penaltySourceBadge((record as any).reference_type, record.is_auto).className
                        }`}
                      >
                        {penaltySourceBadge((record as any).reference_type, record.is_auto).label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {record.is_cancelled ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                          취소됨
                        </span>
                      ) : record.reduced_points && record.reduced_points > 0 ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                          감경됨
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                          유효
                        </span>
                      )}
                    </td>
                    {/* 작업 칸의 버튼들은 행 클릭(상세보기)과 겹치지 않게 막는다 */}
                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      {!record.is_cancelled ? (
                        <div className="flex items-center justify-center gap-2">
                          {record.reduced_points && record.reduced_points > 0 ? (
                            <>
                              <button
                                onClick={() => openReduceModal(record)}
                                className="text-sm text-indigo-600 hover:text-indigo-700"
                              >
                                감경수정
                              </button>
                              {onCancelReduction && (
                                <button
                                  onClick={async () => {
                                    if (window.confirm('감경을 취소하고 원래 점수로 복구하시겠습니까?')) {
                                      try {
                                        await onCancelReduction(record.id);
                                      } catch (e) {
                                        console.error('감경 취소 실패:', e);
                                      }
                                    }
                                  }}
                                  className="text-sm text-orange-600 hover:text-orange-700"
                                >
                                  감경취소
                                </button>
                              )}
                            </>
                          ) : getMaxReducePoints(record) >= 1 ? (
                            <button
                              onClick={() => openReduceModal(record)}
                              className="text-sm text-indigo-600 hover:text-indigo-700"
                            >
                              감경
                            </button>
                          ) : null}
                          <button
                            onClick={() => setCancelingId(record.id)}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            취소
                          </button>
                        </div>
                      ) : onRestoreRecord ? (
                        <button
                          onClick={async () => {
                            if (window.confirm('이 벌점을 복구하시겠습니까?')) {
                              try {
                                await onRestoreRecord(record.id);
                              } catch (e) {
                                console.error('벌점 복구 실패:', e);
                              }
                            }
                          }}
                          className="text-sm text-blue-600 hover:text-blue-700"
                        >
                          복구
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t flex items-center justify-between">
              <span className="text-sm text-gray-500">
                총 {total}건 중 {(currentPage - 1) * PAGE_SIZE + 1}-{Math.min(currentPage * PAGE_SIZE, total)}건
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => onPageChange(1)}
                  disabled={currentPage === 1}
                  className="px-2 py-1 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  처음
                </button>
                <button
                  onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  이전
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => Math.abs(p - currentPage) <= 2)
                  .map(p => (
                    <button
                      key={p}
                      onClick={() => onPageChange(p)}
                      className={`px-3 py-1 text-sm rounded border ${
                        p === currentPage
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                <button
                  onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  다음
                </button>
                <button
                  onClick={() => onPageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-2 py-1 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  마지막
                </button>
              </div>
            </div>
          )}
        </>
        )}
      </div>

      {/* 취소 모달 */}
      {cancelingId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">벌점 취소</h3>
            <p className="text-sm text-gray-500 mb-4">취소 사유를 입력해주세요.</p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="취소 사유를 입력하세요..."
              className="w-full border rounded-lg p-3 text-sm"
              rows={3}
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setCancelingId(null);
                  setCancelReason('');
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                닫기
              </button>
              <button
                onClick={handleCancel}
                disabled={!cancelReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                취소하기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 감경 모달 */}
      {reducingRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">벌점 감경</h3>

            {/* 현재 기록 정보 */}
            <div className="bg-gray-50 rounded-lg p-3 mb-4 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">학생</span>
                <span className="font-medium text-gray-900">{reducingRecord.student_name || '학생'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">벌점 유형</span>
                <span className="font-medium text-gray-900">{getTypeName(reducingRecord.penalty_type)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">현재 점수</span>
                <span className={`font-medium ${
                  (reducingRecord.point_type || 'demerit') === 'merit' ? 'text-blue-600' : 'text-red-600'
                }`}>
                  {getEffectivePoints(reducingRecord)}점
                </span>
              </div>
            </div>

            {/* 감경 점수 입력 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">감경할 점수</label>
              <input
                type="number"
                value={reducePoints}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 0;
                  const max = getMaxReducePoints(reducingRecord);
                  setReducePoints(Math.min(Math.max(1, val), max));
                }}
                min={1}
                max={getMaxReducePoints(reducingRecord)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">
                최소 1점, 최대 {getMaxReducePoints(reducingRecord)}점 감경 가능
              </p>
            </div>

            {/* 미리보기 */}
            <div className="bg-indigo-50 rounded-lg p-3 mb-4 text-center">
              <span className="text-sm text-indigo-700 font-medium">
                {getEffectivePoints(reducingRecord)}점 {'\u2192'} {getEffectivePoints(reducingRecord) - reducePoints}점
                <span className="text-indigo-500 ml-1">({reducePoints}점 감경)</span>
              </span>
            </div>

            {/* 감경 사유 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">감경 사유</label>
              <textarea
                value={reduceReason}
                onChange={(e) => setReduceReason(e.target.value)}
                placeholder="감경 사유를 입력하세요..."
                className="w-full border rounded-lg p-3 text-sm"
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setReducingRecord(null);
                  setReducePoints(1);
                  setReduceReason('');
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                닫기
              </button>
              <button
                onClick={handleReduce}
                disabled={!reduceReason.trim() || reducePoints < 1}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                감경하기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 상세보기 — 예정 시각 / 실제 도착 시각 / 지각 시간 / 그날 입·퇴실 기록 */}
      {detailRecordId && (
        <PenaltyDetailModal
          recordId={detailRecordId}
          onClose={() => setDetailRecordId(null)}
        />
      )}
    </div>
  );
};
